import React, { useState } from "react";
import { useInsights } from "../hooks/useInsights";

const Z = {
  primary:             "#55624d",
  primaryFixed:        "#d9e7cd",
  primaryFixedDim:     "#bdcbb2",
  onPrimary:           "#ffffff",
  surface:             "#f8faf3",
  surfaceContainer:    "#ecefe8",
  surfaceContainerLow: "#f2f4ed",
  surfaceContainerLowest: "#ffffff",
  surfaceContainerHigh:"#e7e9e2",
  onSurface:           "#191c18",
  onSurfaceVariant:    "#444841",
  secondary:           "#755754",
  secondaryContainer:  "#fed7d2",
  onSecondaryContainer:"#795b58",
  outline:             "#757870",
  outlineVariant:      "#c5c8be",
  error:               "#ba1a1a",
  errorContainer:      "#ffdad6",
  inverseSurface:      "#2e312d",
  inverseOnSurface:    "#eff2ea",
};

const STATUS_COLOR = {
  complete:   Z.primary,
  freeze:     "#3b82f6",
  grace:      "#f59e0b",
  missed:     Z.error,
  incomplete: Z.outlineVariant,
  empty:      Z.surfaceContainerHigh,
};

// ─────────────────────────────────────────────
// KOMPONENTEN
// ─────────────────────────────────────────────
function Card({ children, style }) {
  return (
    <div style={{
      background: Z.surfaceContainerLowest,
      borderRadius: 20, padding: "18px 18px",
      border: `1px solid ${Z.outlineVariant}`,
      boxShadow: "0 2px 12px rgba(85,98,77,0.04)",
      ...style,
    }}>
      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: Z.outline, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {children}
    </div>
  );
}

function StatPill({ icon, value, label, accent }) {
  return (
    <div style={{ flex: 1, background: accent || Z.surfaceContainerLow, borderRadius: 16, padding: "16px 12px", textAlign: "center", border: `1px solid ${Z.outlineVariant}` }}>
      <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontFamily: "'Manrope', sans-serif", fontSize: 22, fontWeight: 700, color: Z.onSurface, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: Z.outline, fontWeight: 600, marginTop: 4, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{label}</div>
    </div>
  );
}

// ─────────────────────────────────────────────
// WOCHENGRAFIK
// ─────────────────────────────────────────────
function WeekChart({ days }) {
  const max = Math.max(...days.map((d) => d.totalCount), 1);
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", height: 120 }}>
      {days.map((day) => {
        const heightPct = day.totalCount > 0 ? (day.doneCount / day.totalCount) : 0;
        const barH = Math.max(4, Math.round(heightPct * 88));
        const color = day.status === "complete" ? Z.primary
          : day.status === "freeze" ? "#3b82f6"
          : day.status === "grace"  ? "#f59e0b"
          : day.status === "missed" ? Z.error
          : day.progress > 0        ? Z.primaryFixedDim
          : Z.surfaceContainerHigh;

        return (
          <div key={day.date} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            {/* Prozent-Label */}
            <span style={{ fontSize: 10, color: day.progress > 0 ? Z.onSurfaceVariant : Z.outlineVariant, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, height: 14 }}>
              {day.progress > 0 ? `${day.progress}%` : ""}
            </span>
            {/* Bar */}
            <div style={{ width: "100%", height: 88, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
              <div style={{
                width: "100%", height: barH,
                background: color,
                borderRadius: 6,
                transition: "height 0.5s ease",
                opacity: day.isToday ? 1 : 0.75,
                outline: day.isToday ? `2px solid ${Z.primary}` : "none",
                outlineOffset: 2,
              }} />
            </div>
            {/* Label */}
            <span style={{ fontSize: 11, color: day.isToday ? Z.primary : Z.outline, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: day.isToday ? 700 : 400 }}>
              {day.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
// HABIT STATS LISTE
// ─────────────────────────────────────────────
function HabitStatRow({ habit, rank }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${Z.outlineVariant}` }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: Z.outline, minWidth: 20, fontFamily: "'Manrope', sans-serif" }}>#{rank}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: Z.onSurface, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {habit.label}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          {/* Rate bar */}
          <div style={{ flex: 1, height: 4, borderRadius: 2, background: Z.surfaceContainerHigh }}>
            <div style={{ width: `${habit.rate}%`, height: "100%", borderRadius: 2, background: Z.primary, transition: "width 0.5s ease" }} />
          </div>
          <span style={{ fontSize: 11, color: Z.outline, fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: 32, textAlign: "right" }}>{habit.rate}%</span>
        </div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        {habit.streak > 0 && (
          <span style={{ fontSize: 11, background: Z.primaryFixed, color: Z.primary, borderRadius: 8, padding: "2px 8px", fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            🔥 {habit.streak}
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// INSIGHTS TAB
// ─────────────────────────────────────────────
export function InsightsTab({ userId, allHabits }) {
  const { insights, loading } = useInsights(userId, allHabits);

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 20px", gap: 12 }}>
        <div style={{ fontSize: 32 }}>📊</div>
        <span style={{ fontSize: 14, color: Z.outline, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Statistiken werden geladen…</span>
      </div>
    );
  }

  if (!insights || insights.activeDays === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 20px", gap: 16, textAlign: "center" }}>
        <div style={{ fontSize: 48 }}>🌱</div>
        <h3 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 20, fontWeight: 500, color: Z.onSurface, margin: 0 }}>Noch keine Daten</h3>
        <p style={{ fontSize: 14, color: Z.onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.6, maxWidth: 280 }}>
          Hake deine ersten Habits ab — nach ein paar Tagen erscheinen hier deine Statistiken.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: "28px 20px", maxWidth: 520, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Titel */}
      <div>
        <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 26, fontWeight: 300, color: Z.primary, letterSpacing: "-0.02em", margin: "0 0 4px" }}>
          Dein Fortschritt
        </h2>
        <p style={{ fontSize: 13, color: Z.onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Letzte 30 Tage · {insights.activeDays} aktive Tage
        </p>
      </div>

      {/* Top Stats */}
      <div style={{ display: "flex", gap: 10 }}>
        <StatPill icon="✅" value={`${insights.completionRate}%`} label="Completion" accent={Z.primaryFixed} />
        <StatPill icon="🔥" value={insights.bestStreak} label="Beste Streak" />
        <StatPill icon="⚡" value={insights.totalChecks} label="Habits done" />
      </div>

      {/* Wochengrafik */}
      <Card>
        <Label>Diese Woche</Label>
        <WeekChart days={insights.last7} />
        {/* Legende */}
        <div style={{ display: "flex", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
          {[["complete", Z.primary, "Vollständig"], ["freeze", "#3b82f6", "Freeze"], ["grace", "#f59e0b", "Grace"], ["missed", Z.error, "Verpasst"]].map(([k, c, l]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: c }} />
              <span style={{ fontSize: 11, color: Z.outline, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{l}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Highlights */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {insights.topHabit && (
          <Card>
            <Label>Stärkster Habit</Label>
            <div style={{ fontSize: 22, marginBottom: 6 }}>🏆</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: Z.onSurface, fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.4 }}>
              {insights.topHabit.label.replace(/^[^\w]*/, "").slice(0, 40)}
            </div>
            <div style={{ fontSize: 12, color: Z.primary, marginTop: 4, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {insights.topHabit.count}× erledigt
            </div>
          </Card>
        )}
        {insights.bestDay && (
          <Card>
            <Label>Bester Tag</Label>
            <div style={{ fontSize: 22, marginBottom: 6 }}>📅</div>
            <div style={{ fontFamily: "'Manrope', sans-serif", fontSize: 28, fontWeight: 600, color: Z.primary }}>{insights.bestDay}</div>
            <div style={{ fontSize: 12, color: Z.onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Meiste Completions
            </div>
          </Card>
        )}
      </div>

      {/* Habit-Rangliste */}
      {insights.habitStats.length > 0 && (
        <Card>
          <Label>Habit-Rangliste · 30 Tage</Label>
          {insights.habitStats.slice(0, 8).map((h, i) => (
            <HabitStatRow key={h.id} habit={h} rank={i + 1} />
          ))}
          {insights.habitStats.length > 8 && (
            <div style={{ fontSize: 12, color: Z.outline, textAlign: "center", marginTop: 10, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              + {insights.habitStats.length - 8} weitere Habits
            </div>
          )}
        </Card>
      )}

      {/* Verbesserungs-Hinweis */}
      {insights.missedHabit && insights.missedHabit.rate < 40 && (
        <div style={{ padding: "16px 18px", background: Z.secondaryContainer, borderRadius: 18, border: `1px solid ${Z.outlineVariant}`, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ fontSize: 20 }}>💡</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: Z.onSecondaryContainer, fontFamily: "'Plus Jakarta Sans', sans-serif', marginBottom: 4" }}>
              Verbesserungspotenzial
            </div>
            <div style={{ fontSize: 13, color: Z.onSecondaryContainer, fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.5 }}>
              „{insights.missedHabit.label.replace(/^[^\w]*/, "").slice(0, 50)}" wird nur {insights.missedHabit.rate}% der Zeit erledigt. Überlege ob der Zeitpunkt oder der Aufwand angepasst werden kann.
            </div>
          </div>
        </div>
      )}

      <div style={{ height: 20 }} />
    </div>
  );
}
